/**
 * Tracking for tables the player has just explicitly left.
 *
 * Why it exists: when the player taps Leave, GameService.leaveTable records
 * the gameID + Date.now() under `localStorage.recentlyExitedTables`. While
 * an entry stands — until the engine confirms the leave, at most
 * EXITED_TABLE_MAX_MS (180s; see below) — these use it, and the table cap
 * leaves the table out of its count (isExitedTable, EN-11131, which also
 * covers the untimed player-exit marks further down):
 *
 *   1. App.tsx — the GOT_TEXAS / GOT_OMAHA queue drops payloads for any
 *      gameID flagged here, so an in-flight broadcast can't re-hydrate the
 *      redux entry (which would re-mount the grid cell with stale "still
 *      seated" state and force the user to click Leave a second time).
 *   2. App.tsx auto-resub effect — when the player-games list arrives,
 *      tables in this set are skipped so an engine-side deferred leave
 *      (e.g. wait-for-big-blind) doesn't immediately re-subscribe us.
 *
 * Entries are pruned lazily — `isRecentlyExitedTable` returns false for
 * stale ones, and the auto-resub effect rewrites the localStorage record
 * to drop expired entries each time it runs. ViewManagerContext.openTable
 * also clears any matching entry when the user explicitly re-opens a table.
 */

export const RECENTLY_EXITED_GRACE_MS = 30000;

export const RECENTLY_EXITED_STORAGE_KEY = 'recentlyExitedTables';

// Tables the player is actively being BALANCED ONTO (the rebalance destination).
// Marked the instant balancing is detected — synchronously, on the same
// gotTnmStatus tick that arms the grid rebind — so it never depends on the
// laggier gotPlayerTournamentDetails feed. While protected, App.tsx's
// closed-table sweep must NOT remove the table and the GOT_TEXAS/OMAHA queue
// must NOT drop its broadcasts, even though the engine's cumulative
// `closedGameIDs` transiently lists it as closed mid-reshuffle. Without this the
// destination's balancing-complete gotTexas (isPostBalancing:0) is swallowed for
// the 30s grace window and the grid cell is stuck on the loading felt forever.
// Short-lived: the balancing move completes in a few seconds; the mark
// auto-expires so it can never wrongly protect a genuinely-closed table later.
export const PROTECTED_TABLE_GRACE_MS = 20000;

export const PROTECTED_TABLES_STORAGE_KEY = 'protectedIncomingTables';

export function isProtectedTable(gameID: string | number): boolean {
  try {
    const stored = JSON.parse(localStorage.getItem(PROTECTED_TABLES_STORAGE_KEY) || '{}');
    const ts = stored?.[String(gameID)];
    return typeof ts === 'number' && Date.now() - ts < PROTECTED_TABLE_GRACE_MS;
  } catch {
    return false;
  }
}

export function markProtectedTable(gameID: string | number): void {
  const key = String(gameID);
  if (!key) return;
  try {
    const stored = JSON.parse(localStorage.getItem(PROTECTED_TABLES_STORAGE_KEY) || '{}');
    stored[key] = Date.now();
    localStorage.setItem(PROTECTED_TABLES_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    /* localStorage unavailable — fail open (no protection). */
  }
}

export function clearProtectedTable(gameID: string | number): void {
  const key = String(gameID);
  if (!key) return;
  try {
    const stored = JSON.parse(localStorage.getItem(PROTECTED_TABLES_STORAGE_KEY) || '{}');
    if (stored && typeof stored === 'object' && key in stored) {
      delete stored[key];
      localStorage.setItem(PROTECTED_TABLES_STORAGE_KEY, JSON.stringify(stored));
    }
  } catch {
    /* noop */
  }
}

// Balancing-move DESTINATION table (only). Written on the balancing branch of
// gotPlayerTournamentDetails in TournamentScreenModal for the incoming table's
// gameID; read by the Table felt's chip loader so it can add the "Balancing
// Tables: Being moved to another table" caption alongside the spinner ONLY on
// the destination cell. Kept SEPARATE from markProtectedTable — that broader
// mark is also set by Re-Entry (handleRebuyChips) and for the SOURCE cell's
// sweep protection, neither of which should surface the caption.
//
// Short grace (matches the source-side 3s setTimeout that completes the move,
// with a little slack) so the caption cannot linger over a hydrated felt if
// the explicit clear ever misses — no more "stuck spinner after the hand
// started" bug. Normal path: markBalancingMoveTable → caption shows →
// clearBalancingMoveTable called explicitly when the balancing setTimeout
// completes. Fallback: mark auto-expires shortly after that anyway.
export const BALANCING_TABLES_STORAGE_KEY = 'balancingMoveTables';

export const BALANCING_TABLE_GRACE_MS = 4000;

export function isBalancingMoveTable(gameID: string | number): boolean {
  try {
    const stored = JSON.parse(localStorage.getItem(BALANCING_TABLES_STORAGE_KEY) || '{}');
    const ts = stored?.[String(gameID)];
    return typeof ts === 'number' && Date.now() - ts < BALANCING_TABLE_GRACE_MS;
  } catch {
    return false;
  }
}

export function markBalancingMoveTable(gameID: string | number): void {
  const key = String(gameID);
  if (!key) return;
  try {
    const stored = JSON.parse(localStorage.getItem(BALANCING_TABLES_STORAGE_KEY) || '{}');
    stored[key] = Date.now();
    localStorage.setItem(BALANCING_TABLES_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    /* localStorage unavailable — fail open (no caption). */
  }
}

export function clearBalancingMoveTable(gameID: string | number): void {
  const key = String(gameID);
  if (!key) return;
  try {
    const stored = JSON.parse(localStorage.getItem(BALANCING_TABLES_STORAGE_KEY) || '{}');
    if (stored && typeof stored === 'object' && key in stored) {
      delete stored[key];
      localStorage.setItem(BALANCING_TABLES_STORAGE_KEY, JSON.stringify(stored));
    }
  } catch {
    /* noop */
  }
}

/**
 * Absolute ceiling on an exit mark.
 *
 * The mark is normally cleared by CONFIRMATION — `confirmTablesReleased` drops
 * it the moment the engine stops listing the table — rather than by a timer.
 * The old fixed 30s window is what let an exited table walk back in: it simply
 * stopped suppressing after half a minute, whether or not the leave had taken
 * effect, and any stale client-side entry then resurfaced. This is only a
 * backstop so a mark can never hide a table forever.
 */
export const EXITED_TABLE_MAX_MS = 180000;

/**
 * Minimum age a mark must reach before an engine "table absent" signal is
 * trusted as a real confirmation. Below this age, a mark is kept even if the
 * current GOT_PLAYER_GAMES payload omits its gameID.
 *
 * This absorbs the engine's ping-pong window on sit-out + leave: wave 1 arrives
 * with the exited table already dropped (looks like the leave landed), then a
 * second wave briefly re-includes it while the engine finishes its own cleanup.
 * Without this gate, the first wave would clear the mark, and the resub loop on
 * the second wave would re-subscribe to the table.
 */
export const CONFIRM_RELEASE_MIN_AGE_MS = 15000;

/**
 * When the next recently-exited mark lapses (ms epoch), or null if none will.
 * A mark that lapses changes isExitedTable with no store update, so anything
 * derived from it in a memo re-derives at this moment (useActiveTables).
 *
 * A mark stamped in the future (the device clock moved back) still counts as
 * active (isRecentlyExitedTable), so it is scheduled too — but no further out
 * than EXITED_TABLE_MAX_MS from now: the re-derive then re-checks it and
 * schedules the next look, keeping every delay within setTimeout's range.
 */
export function nextRecentlyExitedExpiry(now: number = Date.now()): number | null {
  try {
    const stored = JSON.parse(localStorage.getItem(RECENTLY_EXITED_STORAGE_KEY) || '{}');
    const expiries = Object.values(stored || {})
      .filter((ts): ts is number => typeof ts === 'number')
      .map((ts) => Math.min(ts, now) + EXITED_TABLE_MAX_MS)
      .filter((at) => at > now);
    return expiries.length ? Math.min(...expiries) : null;
  } catch {
    return null;
  }
}

export function isRecentlyExitedTable(gameID: string): boolean {
  try {
    const stored = JSON.parse(localStorage.getItem(RECENTLY_EXITED_STORAGE_KEY) || '{}');
    const ts = stored?.[String(gameID)];
    return typeof ts === 'number' && Date.now() - ts < EXITED_TABLE_MAX_MS;
  } catch {
    return false;
  }
}

/**
 * Reconcile the exit marks against the engine's own list of the player's games.
 *
 * An exited table stays suppressed until the engine confirms it — i.e. until it
 * disappears from `GOT_PLAYER_GAMES` — AND the mark has aged past
 * `CONFIRM_RELEASE_MIN_AGE_MS`. Confirmation on a very young mark is treated as
 * a possible engine ping-pong (see the constant's docstring) and ignored until
 * the mark is old enough to trust. Anything past the absolute ceiling is
 * dropped regardless.
 *
 * @param presentGameIDs gameIDs in the latest player-games payload.
 * @returns the gameIDs still suppressed, for the caller's resubscribe filter.
 */
export function confirmTablesReleased(presentGameIDs: Iterable<string | number>): Set<string> {
  const present = new Set(Array.from(presentGameIDs, (id) => String(id)));
  const now = Date.now();
  try {
    const stored = JSON.parse(localStorage.getItem(RECENTLY_EXITED_STORAGE_KEY) || '{}');
    const kept: Record<string, number> = {};
    Object.entries(stored || {}).forEach(([gid, ts]) => {
      if (typeof ts !== 'number') return;
      const age = now - ts;
      // Backstop: never suppress indefinitely.
      if (age >= EXITED_TABLE_MAX_MS) return;
      // The engine has stopped reporting it → the leave landed, BUT only trust
      // that once the mark is old enough to have outlived the sit-out cleanup
      // ping-pong. Young marks are kept regardless of what the payload says.
      if (!present.has(gid) && age >= CONFIRM_RELEASE_MIN_AGE_MS) return;
      kept[gid] = ts;
    });
    localStorage.setItem(RECENTLY_EXITED_STORAGE_KEY, JSON.stringify(kept));
    return new Set(Object.keys(kept));
  } catch {
    return new Set();
  }
}

/**
 * Flag a gameID as recently-exited so App.tsx's GOT_TEXAS / GOT_OMAHA queue
 * drops in-flight broadcasts AND the auto-resub effect skips re-subscribing
 * within the grace window. Used by paths that want the same "block stale
 * re-hydration" guarantees as an explicit Leave Table, without going through
 * the seat-bound GameService.leaveTable (e.g. ViewManagerProvider tearing
 * down an observer slot for an active-player tournament restriction).
 */
export function markRecentlyExitedTable(gameID: string | number): void {
  const key = String(gameID);
  if (!key) return;
  try {
    const stored = JSON.parse(localStorage.getItem(RECENTLY_EXITED_STORAGE_KEY) || '{}');
    stored[key] = Date.now();
    localStorage.setItem(RECENTLY_EXITED_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    /* localStorage unavailable — fail open; the watcher will keep firing
       and eventually the unsubscribe will land server-side. */
  }
}

/**
 * Drop a gameID's recently-exited mark so App.tsx stops dropping its
 * GOT_TEXAS / GOT_OMAHA broadcasts and the auto-resub re-subscribes it.
 *
 * Needed whenever the player is deliberately (re)entering a table that may
 * still be inside the 30s grace window from a prior exit — e.g. tournament
 * balancing that moves the player BACK to a table they were recently balanced
 * away from. Without clearing the mark, the new table's broadcasts are
 * swallowed and its grid cell never hydrates. ViewManagerContext.openTable
 * performs the equivalent clear for the single-table navigation path.
 */
export function clearRecentlyExitedTable(gameID: string | number): void {
  const key = String(gameID);
  if (!key) return;
  try {
    const stored = JSON.parse(localStorage.getItem(RECENTLY_EXITED_STORAGE_KEY) || '{}');
    if (stored && typeof stored === 'object' && key in stored) {
      delete stored[key];
      localStorage.setItem(RECENTLY_EXITED_STORAGE_KEY, JSON.stringify(stored));
    }
  } catch {
    /* noop */
  }
}

// ── Tables the player exited ─────────────────────────────────────────────────
// The player closed the table themselves (Exit Table: useLeaveTable, and the
// Buy Back In modal), and the leave was sent — an unsent leave leaves them
// seated, so it records nothing. It stays out of the
// nav, and is not re-subscribed, for as long as the engine still lists them
// there — a deferred leave waiting for the big blind, or waiting to be removed
// — however long that takes, and across a refresh. Unlike the recently-exited
// mark above (a 180s ceiling, cleared on every page load, and also set by
// engine-driven closures), this is set only by the player's own exit and ends
// only when they open the table again (ViewManagerContext.openTable) or the
// engine confirms the leave (the table leaves GOT_PLAYER_GAMES). Cleared on
// logout with the rest of the member's state (clearAuth).
export const PLAYER_EXITED_STORAGE_KEY = 'playerExitedTables';

/**
 * One exit. `heldSeatIn`: the tournament whose seat the player still held at
 * this table when they exited (their details had them playing, status 'P',
 * here) — e.g. Exit Table from the load-error modal. While the engine keeps
 * listing that seat the leave is still pending, so a 'P' for it must not undo
 * the exit; once the engine drops the seat the field is removed, and a later
 * 'P' here is a new seating (see releaseExitsForAssignedSeats).
 */
type PlayerExit = { at: number; heldSeatIn?: number; outOfTournament?: true };

/**
 * How long an exit from a table the player was out of the tournament at
 * (`outOfTournament`) waits before a table absent from GOT_PLAYER_GAMES drops
 * it. Not what ends it — opening the table again or a new seat there does —
 * only a bound on the stored record, far beyond any Sit & Go's life.
 */
export const OUT_OF_TOURNAMENT_EXIT_MAX_MS = 12 * 60 * 60 * 1000;

const readPlayerExited = (): Record<string, PlayerExit> => {
  try {
    const stored = JSON.parse(localStorage.getItem(PLAYER_EXITED_STORAGE_KEY) || '{}');
    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return {};
    const out: Record<string, PlayerExit> = {};
    Object.entries(stored).forEach(([gid, v]: [string, any]) => {
      if (typeof v === 'number') out[gid] = { at: v };
      else if (v && typeof v.at === 'number') out[gid] = v;
    });
    return out;
  } catch {
    return {};
  }
};

const writePlayerExited = (stored: Record<string, PlayerExit>): void => {
  try {
    localStorage.setItem(PLAYER_EXITED_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    /* localStorage unavailable — the recently-exited mark still covers 180s. */
  }
};

/**
 * The player exited this table: keep it out of the nav until they reopen it.
 * `heldSeatIn`: the tournament they were still playing at this table, if any.
 *
 * `outOfTournament`: they were out of the tournament at this table (busted,
 * or it finished) — a Sit & Go result's No or Go to lobby. No leave is
 * pending there, so the engine not listing the table proves nothing, and its
 * listing it again is never a join: it keeps a busted player's table in
 * GOT_PLAYER_GAMES while listing a texas table only within 20 s of its last
 * player_games write, so the table drops out and comes back. Such an exit is
 * not ended by the table's absence (see confirmPlayerExitsReleased); opening
 * the table, or a seat there (a re-entry), still ends it.
 */
export function markPlayerExitedTable(
  gameID: string | number,
  opts: { heldSeatIn?: number; outOfTournament?: boolean } = {}
): void {
  const key = String(gameID);
  if (!key) return;
  const exit: PlayerExit = { at: Date.now() };
  if (Number(opts.heldSeatIn) > 0) exit.heldSeatIn = Number(opts.heldSeatIn);
  if (opts.outOfTournament) exit.outOfTournament = true;
  writePlayerExited({ ...readPlayerExited(), [key]: exit });
}

/** Whether the player exited this table and hasn't opened it again. */
export function isPlayerExitedTable(gameID: string | number): boolean {
  return String(gameID) in readPlayerExited();
}

/**
 * GOT_PLAYER_GAMES's exit handling, in its order: reconcile both kinds of exit
 * mark against the tables the engine lists (`presentGameIDs`), then return the
 * listed tables that must not be re-subscribed — just left, or exited by the
 * player and still listed (e.g. a deferred leave). A confirmed leave is no
 * longer returned, so a later join of that table subscribes normally.
 */
export function reconcileExitsWithPlayerGames(
  presentGameIDs: Iterable<string | number>
): Set<string> {
  const present = Array.from(presentGameIDs, (id) => String(id));
  const suppressed = confirmTablesReleased(present);
  confirmPlayerExitsReleased(present);
  present.filter((gid) => isPlayerExitedTable(gid)).forEach((gid) => suppressed.add(gid));
  return suppressed;
}

/**
 * Whether a table is kept out of the player's tables: just left (in-flight
 * broadcasts can re-add it), or exited by the player and not opened again —
 * even while the engine still has them seated, waiting to be removed.
 */
export function isExitedTable(gameID: string | number): boolean {
  return isRecentlyExitedTable(String(gameID)) || isPlayerExitedTable(gameID);
}

/** The player opened the table again. */
export function clearPlayerExitedTable(gameID: string | number): void {
  const key = String(gameID);
  const stored = readPlayerExited();
  if (!(key in stored)) return;
  delete stored[key];
  writePlayerExited(stored);
}

/**
 * GOT_PLAYERTOURNAMENTDETAILS's exit handling, run before the handler records
 * this push's seats. A seat the engine assigns (status 'P') supersedes an
 * earlier exit from that gameID — the player busted and exited, then was
 * re-entered or rebalanced back onto it — which would otherwise keep the table
 * they now sit at unsubscribed and its broadcasts dropped, so it never loads
 * or shows in the nav.
 *
 * Except the seat the player exited FROM: an exit recorded while they still
 * held the seat (`heldSeatIn`) stands while this push still lists that same
 * seat — the leave is pending, and a refresh doesn't change that. When a push
 * shows the tournament elsewhere (another table, or not playing), the seat was
 * dropped: the exit stops holding it, and a later 'P' here is a new seating.
 *
 * For a seat that does supersede an exit, the player's own exit is cleared,
 * and the recently-exited mark too if the seat is NEW — not among the seats the
 * handler already tracked (`isKnownSeat`): a mark on a seat it already knew was
 * set after that assignment (the table closed, say), so it still stands.
 */
export function releaseExitsForAssignedSeats(
  details: unknown,
  isKnownSeat: (tournamentId: number, gameID: number) => boolean
): void {
  if (!Array.isArray(details)) return;
  const playing = new Map<number, number>(); // tournamentId -> gameID, status 'P'
  const listed = new Set<number>(); // tournaments this push reports on
  details.forEach((d) => {
    const tournamentId = Number(d?.tournamentId);
    if (!(tournamentId > 0)) return;
    listed.add(tournamentId);
    if (d?.status === 'P' && Number(d?.gameID) > 0) playing.set(tournamentId, Number(d.gameID));
  });

  // A held seat the engine has dropped: the tournament is reported, but not
  // as playing at that table any more.
  const exits = readPlayerExited();
  let dropped = false;
  Object.entries(exits).forEach(([gid, exit]) => {
    const t = exit.heldSeatIn;
    if (t && listed.has(t) && playing.get(t) !== Number(gid)) {
      delete exit.heldSeatIn;
      dropped = true;
    }
  });
  if (dropped) writePlayerExited(exits);

  playing.forEach((gameID, tournamentId) => {
    const exit = exits[String(gameID)];
    if (exit?.heldSeatIn === tournamentId) return; // the leave is still pending
    clearPlayerExitedTable(gameID);
    if (!isKnownSeat(tournamentId, gameID)) clearRecentlyExitedTable(gameID);
  });
}

/**
 * Drop the exits the engine has confirmed: the table is no longer in the
 * player's games (`presentGameIDs`, from GOT_PLAYER_GAMES). A young mark is
 * kept regardless — the engine can drop and re-list a table while it finishes
 * a leave (see CONFIRM_RELEASE_MIN_AGE_MS). An exit from a table the player
 * was out of the tournament at has no leave to confirm, so the table being
 * absent ends it only past OUT_OF_TOURNAMENT_EXIT_MAX_MS.
 */
export function confirmPlayerExitsReleased(presentGameIDs: Iterable<string | number>): void {
  const present = new Set(Array.from(presentGameIDs, (id) => String(id)));
  const now = Date.now();
  const stored = readPlayerExited();
  const kept: Record<string, PlayerExit> = {};
  Object.entries(stored).forEach(([gid, exit]) => {
    const releaseAge = exit.outOfTournament
      ? OUT_OF_TOURNAMENT_EXIT_MAX_MS
      : CONFIRM_RELEASE_MIN_AGE_MS;
    if (!present.has(gid) && now - exit.at >= releaseAge) return;
    kept[gid] = exit;
  });
  if (Object.keys(kept).length !== Object.keys(stored).length) writePlayerExited(kept);
}

// ── Reconciled-away tables ───────────────────────────────────────────────────
// Session-scoped set of tables the client has AUTO-CLOSED because the player has
// an active seat ELSEWHERE in that tournament (see computeObservedTablesToClose):
// the old seat after a rebalance, or tables observed while busted, once the
// player is actively playing again. Unlike recentlyExitedTables (a 30s grace),
// this persists for the page session — the engine keeps observers in playerGames
// even after UNSUBSCRIBE, so the auto-resub / a late GOT_TEXAS would otherwise
// resurrect the table the moment the 30s grace lapses, and after the player busts
// AGAIN nothing re-cleans it (the "wrong-tournament" cleanup only fires for a 'P'
// player). Reported: the auto-closed table popped back with a stuck "Please wait"
// modal after busting out again. A module Set (not localStorage) is deliberate:
// it should reset on a real page refresh, where playerGames is re-fetched fresh.
const reconciledAwayTables = new Set<string>();

/** Mark a table as auto-closed for the session so it can't be resurrected. */
export function markReconciledAwayTable(gameID: string | number): void {
  const key = String(gameID);
  if (key) reconciledAwayTables.add(key);
}

/** Whether a table was auto-closed this session (block re-hydration / resub). */
export function isReconciledAwayTable(gameID: string | number): boolean {
  return reconciledAwayTables.has(String(gameID));
}

/** Un-mark a table — the player is genuinely (re)seated there again. */
export function clearReconciledAwayTable(gameID: string | number): void {
  reconciledAwayTables.delete(String(gameID));
}

/** Test-only reset of the session set. */
export function __resetReconciledAwayTables(): void {
  reconciledAwayTables.clear();
}
